
import { useState, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';
import { ChargingStation } from '@/types/task';
import { supabase } from '@/integrations/supabase/client';
import { initialStations } from '@/data/stations';

interface StationDataProviderProps {
  children: (props: {
    stations: ChargingStation[];
    filteredStations: ChargingStation[];
    isLoading: boolean;
    isRefreshing: boolean;
    handleRefresh: () => void;
    setFilteredStations: React.Dispatch<React.SetStateAction<ChargingStation[]>>;
  }) => React.ReactNode;
}

const StationDataProvider = ({ children }: StationDataProviderProps) => {
  const [stations, setStations] = useState<ChargingStation[]>([]);
  const [filteredStations, setFilteredStations] = useState<ChargingStation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { toast } = useToast();

  // Helper function to validate and convert chargerType to the expected union type
  const validateChargerType = (type: string): 'fast' | 'standard' | 'ultra-fast' => {
    switch (type.toLowerCase()) {
      case 'fast':
        return 'fast';
      case 'ultra-fast':
      case 'ultrafast':
      case 'ultra':
        return 'ultra-fast';
      default:
        return 'standard'; // Default fallback
    }
  };

  const fetchStations = async () => {
    try {
      setIsLoading(true);
      let stationsData = [...initialStations];
      
      try {
        const { data, error } = await supabase
          .from('stations')
          .select('*');

        if (!error && data && data.length > 0) {
          stationsData = data.map(station => {
            // Handle coordinates conversion
            let coordinates = { latitude: 37.7749, longitude: -122.4194 }; // Default SF coordinates
            if (station.coordinates && typeof station.coordinates === 'object') {
              if ('latitude' in station.coordinates && 'longitude' in station.coordinates) {
                coordinates = station.coordinates as { latitude: number; longitude: number };
              }
            }

            return {
              ...station,
              rating: station.rating || 0,
              reviews: [],
              images: initialStations[0].images,
              photos: initialStations[0].photos,
              pricePerKwh: station.price || 0,
              checkins: 0,
              description: 'High-quality charging station with reliable service.',
              coordinates,
              powerOutput: station.powerOutput || 50,
              amenities: ['WiFi', 'Restrooms'],
              plugTypes: station.plugTypes || ['CCS'],
              paymentTypes: station.paymentTypes || ['Credit Card'],
              chargerType: validateChargerType(station.chargerType || 'standard'),
              hostId: 'host-' + station.id,
              hostName: 'Station Owner',
              aiGeneratedImage: station.aigeneratedimage || null,
              imagePrompt: station.imageprompt || null
            } as ChargingStation;
          });
        }
      } catch (error) {
        console.log('Using initial stations due to Supabase error:', error);
      }
      
      setStations(stationsData);
      setFilteredStations(stationsData);
      setIsLoading(false);
      setIsRefreshing(false);
    } catch (error) {
      console.error('Error loading stations:', error);
      toast({
        title: 'Error',
        description: 'Failed to load charging stations',
        variant: 'destructive',
      });
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    fetchStations();
  }, [toast]);

  const handleRefresh = () => {
    setIsRefreshing(true);
    fetchStations();
    toast({
      title: 'Refreshing',
      description: 'Updating charging station data',
    });
  };

  return children({
    stations,
    filteredStations,
    isLoading,
    isRefreshing,
    handleRefresh,
    setFilteredStations,
  });
};

export default StationDataProvider;
